/**
 * Pixelfed direct instance API for fetching profile posts and threads
 *
 * Mirrors mastodonService.ts for Pixelfed instances. Pixelfed implements the
 * Mastodon API, so the same types (MastodonStatus, MastodonContext, etc.) and
 * shared utilities (parseLinkHeader, normalizeAcct, stripHTML) apply.
 */
import type { MastodonStatus, MastodonContext, MastodonQuote } from '@/types/mastodon'
import type { Post, PostEmbed, PostsPage } from '@/types/posts'
import type { Thread, ThreadPost } from '@/types/thread'
import {
    type PixelfedSession,
    ensureValidSession,
    instanceUrlFromHandle,
    lookupAccountOnInstance,
    parseLinkHeader,
    normalizeAcct,
    stripHTML,
} from '@/lib/pixelfed'
import { stripQuoteInline } from '@/services/postsService'
import {
    bookmarkMastodonStatus,
    unbookmarkMastodonStatus,
    fetchAPBookmarks,
} from '@/services/mastodonService'
import type { MastodonSession } from '@/lib/mastodon'
import { isSafeHostname } from '@/lib/apUtils'
import { contentWarningFromMastodon } from '@/lib/contentLabels'
import { deriveATBridgeFromAcct, isTrustedBridgeOrigin } from '@/lib/apBridgedIdentity'

const FETCH_TIMEOUT_MS = 10_000
const DEFAULT_LIMIT = 25
const ACCOUNT_CACHE_MAX = 100

interface CachedAccount {
    id: string
    displayName?: string
    avatarUrl?: string
}

/** In-memory cache for account lookups to avoid redundant requests during pagination */
const accountCache = new Map<string, CachedAccount>()

function setCachedAccount(key: string, account: CachedAccount): void {
    if (accountCache.size >= ACCOUNT_CACHE_MAX) {
        const firstKey = accountCache.keys().next().value
        if (firstKey !== undefined) accountCache.delete(firstKey)
    }
    accountCache.set(key, account)
}

/**
 * Fetch statuses for an account directly from a Pixelfed instance (unauthenticated).
 *
 * @returns Object with statuses array and optional next page URL
 */
export async function fetchPixelfedStatuses(
    instanceUrl: string,
    accountId: string,
    options?: { maxId?: string; limit?: number; accessToken?: string }
): Promise<{ statuses: MastodonStatus[]; nextUrl?: string }> {
    const hostname = new URL(instanceUrl).hostname
    if (!isSafeHostname(hostname)) {
        throw new Error(`Unsafe hostname: ${hostname}`)
    }

    const params = new URLSearchParams()
    params.set('limit', String(options?.limit ?? DEFAULT_LIMIT))
    if (options?.maxId) {
        params.set('max_id', options.maxId)
    }
    // Exclude replies by default for profile posts feed
    params.set('exclude_replies', 'true')

    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS)

    try {
        const url = `${instanceUrl}/api/v1/accounts/${encodeURIComponent(accountId)}/statuses?${params}`
        const headers: Record<string, string> = {}
        if (options?.accessToken) {
            headers.Authorization = `Bearer ${options.accessToken}`
        }
        const res = await fetch(url, { headers, signal: controller.signal })

        if (!res.ok) {
            throw new Error(`Pixelfed API error: ${res.status}`)
        }

        const statuses: MastodonStatus[] = await res.json()
        const nextUrl = parseLinkHeader(res.headers.get('Link'))

        return { statuses, nextUrl }
    } finally {
        clearTimeout(timeout)
    }
}

/**
 * Fetch the authenticated user's Pixelfed home timeline.
 *
 * Requires a valid PixelfedSession (auto-refreshes expired tokens).
 * Uses the same pagination pattern as fetchPixelfedStatuses (Link header -> cursor).
 */
export type PixelfedTimelineType = 'home' | 'local' | 'federated'

export async function fetchPixelfedTimeline(
    pixelfedSession: PixelfedSession,
    options?: { cursor?: string; limit?: number; timeline?: PixelfedTimelineType }
): Promise<PostsPage> {
    const session = await ensureValidSession(pixelfedSession)

    const parsed = new URL(session.instance_url)
    if (parsed.protocol !== 'https:') {
        throw new Error(`Unsafe URL scheme: ${parsed.protocol}`)
    }
    if (!isSafeHostname(parsed.hostname)) {
        throw new Error(`Unsafe hostname: ${parsed.hostname}`)
    }

    const params = new URLSearchParams()
    params.set('limit', String(options?.limit ?? DEFAULT_LIMIT))
    if (options?.cursor) {
        params.set('max_id', options.cursor)
    }

    const timelineType = options?.timeline ?? 'home'
    let timelinePath: string
    if (timelineType === 'home') {
        timelinePath = '/api/v1/timelines/home'
    } else {
        if (timelineType === 'local') {
            params.set('local', 'true')
        }
        timelinePath = '/api/v1/timelines/public'
    }

    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS)

    try {
        const url = `${session.instance_url}${timelinePath}?${params}`
        const res = await fetch(url, {
            headers: { Authorization: `Bearer ${session.access_token}` },
            signal: controller.signal,
        })

        if (!res.ok) {
            throw new Error(`Pixelfed API error: ${res.status}`)
        }

        const statuses: MastodonStatus[] = await res.json()
        const nextUrl = parseLinkHeader(res.headers.get('Link'))

        const posts = statuses.map(s => mapPixelfedStatus(s, session.instance_url))

        // Extract cursor from the next page URL's max_id param.
        // Only trust URLs from the same origin to prevent token leakage via crafted Link headers.
        let cursor: string | undefined
        if (nextUrl) {
            try {
                const nextParsed = new URL(nextUrl)
                if (nextParsed.origin === parsed.origin) {
                    cursor = nextParsed.searchParams.get('max_id') ?? undefined
                } else {
                    cursor = statuses.length > 0 ? statuses[statuses.length - 1].id : undefined
                }
            } catch {
                // If URL parsing fails, use the last status ID as cursor
                if (statuses.length > 0) {
                    cursor = statuses[statuses.length - 1].id
                }
            }
        }

        return { posts, cursor }
    } finally {
        clearTimeout(timeout)
    }
}

/**
 * Fetch the authenticated user's favourited statuses.
 *
 * Uses the same session validation, SSRF protection, and pagination pattern
 * as fetchPixelfedTimeline.
 */
export async function fetchPixelfedFavourites(
    pixelfedSession: PixelfedSession,
    options?: { cursor?: string; limit?: number }
): Promise<PostsPage> {
    const session = await ensureValidSession(pixelfedSession)

    const parsed = new URL(session.instance_url)
    if (parsed.protocol !== 'https:') {
        throw new Error(`Unsafe URL scheme: ${parsed.protocol}`)
    }
    if (!isSafeHostname(parsed.hostname)) {
        throw new Error(`Unsafe hostname: ${parsed.hostname}`)
    }

    const params = new URLSearchParams()
    params.set('limit', String(options?.limit ?? DEFAULT_LIMIT))
    if (options?.cursor) {
        params.set('max_id', options.cursor)
    }

    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS)

    try {
        const url = `${session.instance_url}/api/v1/favourites?${params}`
        const res = await fetch(url, {
            headers: { Authorization: `Bearer ${session.access_token}` },
            signal: controller.signal,
        })

        if (!res.ok) {
            throw new Error(`Pixelfed API error: ${res.status}`)
        }

        const statuses: MastodonStatus[] = await res.json()
        const nextUrl = parseLinkHeader(res.headers.get('Link'))

        const posts = statuses.map(s => mapPixelfedStatus(s, session.instance_url))

        let cursor: string | undefined
        if (nextUrl) {
            try {
                const nextParsed = new URL(nextUrl)
                if (nextParsed.origin === parsed.origin) {
                    cursor = nextParsed.searchParams.get('max_id') ?? undefined
                } else {
                    cursor = statuses.length > 0 ? statuses[statuses.length - 1].id : undefined
                }
            } catch {
                if (statuses.length > 0) {
                    cursor = statuses[statuses.length - 1].id
                }
            }
        }

        return { posts, cursor }
    } finally {
        clearTimeout(timeout)
    }
}

/**
 * Resolve a Pixelfed account acct field to a display handle.
 *
 * Bridged AT→AP accounts appear in the Pixelfed API with a Bridgy Fed handle
 * (e.g. "whiteside.io@bsky.brid.gy"). In that case, the username field contains
 * the clean Bluesky handle (e.g. "whiteside.io") which we prefer.
 *
 * AP-native accounts use normalizeAcct to add @instance for local users.
 */
function resolveAcctHandle(
    acct: string,
    instanceUrl: string,
    username?: string,
    isTrustedOrigin = true
): string {
    const bridge = deriveATBridgeFromAcct(acct, username, isTrustedOrigin)
    if (bridge.isBridged) return bridge.handle
    // AP-native: normalizeAcct adds @instance for local users
    return normalizeAcct(acct, instanceUrl)
}

/**
 * Map a Pixelfed status to our normalized Post type.
 *
 * Pixelfed uses the same Mastodon API types. Posts are marked with
 * isActivityPub: true since Pixelfed is an ActivityPub platform.
 * The mastodonStatusId field is reused for the Pixelfed status ID
 * (same API shape).
 *
 * fetchedInstanceUrl is the instance the status JSON was fetched from and is the
 * only source for a local-form `acct`'s domain; `status.uri` is remote-controlled
 * and must never be consulted for identity.
 */
export function mapPixelfedStatus(
    status: MastodonStatus,
    fetchedInstanceUrl: string,
    profileAuthor?: Post['author'],
    isTrustedOrigin = true
): Post {
    // For boosts, the reblogged status is the actual content
    const isBoost = !!status.reblog
    const contentStatus = status.reblog ?? status

    // Only strip quote-inline fallback text when we'll actually render the embed
    const isAcceptedQuote =
        contentStatus.quote?.state === 'accepted' && !!contentStatus.quote.quoted_status
    const htmlContent = isAcceptedQuote
        ? stripQuoteInline(contentStatus.content)
        : contentStatus.content
    const text = stripHTML(htmlContent)

    const bridge = deriveATBridgeFromAcct(
        contentStatus.account.acct,
        contentStatus.account.username,
        isTrustedOrigin
    )
    const normalizedHandle = bridge.isBridged
        ? bridge.handle
        : normalizeAcct(contentStatus.account.acct, fetchedInstanceUrl)

    const author: Post['author'] = {
        did: '',
        handle: normalizedHandle,
        displayHandle: bridge.isBridged ? bridge.handle : normalizedHandle,
        displayName: contentStatus.account.display_name || undefined,
        avatarUrl: contentStatus.account.avatar || undefined,
        bridgeStatus: bridge.isBridged
            ? {
                  isBridged: true,
                  sourceProtocol: 'atprotocol',
                  activityPubHandle: bridge.activityPubHandle,
              }
            : undefined,
    }

    let boostedBy: Post['boostedBy']
    if (isBoost) {
        const boosterBridge = deriveATBridgeFromAcct(
            status.account.acct,
            status.account.username,
            isTrustedOrigin
        )
        const boosterNormalized = boosterBridge.isBridged
            ? boosterBridge.handle
            : normalizeAcct(status.account.acct, fetchedInstanceUrl)
        const boosterHandle = profileAuthor?.handle ?? boosterNormalized
        boostedBy = {
            handle: boosterHandle,
            displayHandle:
                profileAuthor?.displayHandle ??
                (boosterBridge.isBridged ? boosterBridge.handle : boosterNormalized),
            displayName: profileAuthor?.displayName ?? (status.account.display_name || undefined),
            avatarUrl: profileAuthor?.avatarUrl ?? (status.account.avatar || undefined),
        }
    }

    // Extract quoted post from Mastodon 4.4+ quote field
    const quotedPost = isAcceptedQuote
        ? mapPixelfedQuote(contentStatus.quote, fetchedInstanceUrl, isTrustedOrigin)
        : undefined

    return {
        uri: contentStatus.uri,
        cid: undefined,
        rkey: undefined,
        mastodonStatusId: contentStatus.id,
        text,
        // Use boost event time for feed ordering (matches GraphQL boostedAt behavior)
        createdAt: status.created_at,
        isReply: !!contentStatus.in_reply_to_id,
        embed: mapPixelfedEmbed(contentStatus),
        quotedPost,
        metrics: {
            likeCount: contentStatus.favourites_count ?? 0,
            repostCount: contentStatus.reblogs_count ?? 0,
            replyCount: contentStatus.replies_count ?? 0,
        },
        author: isBoost ? author : (profileAuthor ?? author),
        boostedBy,
        likeUri: contentStatus.favourited ? `mastodon:favourited:${contentStatus.id}` : null,
        repostUri: contentStatus.reblogged ? `mastodon:reblogged:${contentStatus.id}` : null,
        bookmarkUri: contentStatus.bookmarked ? `pixelfed:bookmarked:${contentStatus.id}` : null,
        isActivityPub: true,
        protocol: 'pixelfed',
        contentWarning: contentWarningFromMastodon(
            contentStatus.sensitive,
            contentStatus.spoiler_text
        ),
    }
}

/**
 * Map media attachments and cards from a Pixelfed status to our PostEmbed type.
 * Images take precedence over cards (same as GraphQL mapper).
 */
export function mapPixelfedEmbed(status: MastodonStatus): PostEmbed | undefined {
    const images = status.media_attachments.filter(a => a.type === 'image')
    if (images.length > 0) {
        return {
            type: 'images',
            images: images.map(img => ({
                url: img.preview_url || img.url,
                fullsize: img.url,
                // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- an empty description is meaningfully "no alt text", same as missing
                alt: img.description || undefined,
                aspectRatio:
                    img.meta?.original?.width && img.meta?.original?.height
                        ? { width: img.meta.original.width, height: img.meta.original.height }
                        : undefined,
            })),
        }
    }

    if (status.card) {
        return {
            type: 'external',
            external: {
                uri: status.card.url,
                title: status.card.title || undefined,
                description: status.card.description || undefined,
                // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- an empty image URL is invalid, same as missing
                thumb: status.card.image || undefined,
            },
        }
    }

    return undefined
}

/**
 * Map a Pixelfed quote to a normalized Post, if the quote is accepted.
 * Returns undefined for non-accepted quotes or missing data.
 *
 * fetchedInstanceUrl is the instance the enclosing status JSON was fetched from and
 * is the only source for a local-form `acct`'s domain; `quote.quoted_status.uri` is
 * remote-controlled and must never be consulted for identity.
 */
function mapPixelfedQuote(
    quote: MastodonQuote | null | undefined,
    fetchedInstanceUrl: string,
    isTrustedOrigin = true
): Post | undefined {
    if (!quote || quote.state !== 'accepted' || !quote.quoted_status) return undefined

    const qs = quote.quoted_status
    const qsText = stripHTML(qs.content)
    const qsBridge = deriveATBridgeFromAcct(qs.account.acct, qs.account.username, isTrustedOrigin)
    const qsNormalizedHandle = qsBridge.isBridged
        ? qsBridge.handle
        : normalizeAcct(qs.account.acct, fetchedInstanceUrl)

    return {
        uri: qs.uri,
        cid: undefined,
        rkey: undefined,
        mastodonStatusId: qs.id,
        text: qsText,
        createdAt: qs.created_at,
        isReply: false,
        embed: mapPixelfedEmbed(qs),
        metrics: {
            likeCount: qs.favourites_count ?? 0,
            repostCount: qs.reblogs_count ?? 0,
            replyCount: qs.replies_count ?? 0,
        },
        author: {
            did: '',
            handle: qsNormalizedHandle,
            displayName: qs.account.display_name || undefined,
            avatarUrl: qs.account.avatar || undefined,
            bridgeStatus: qsBridge.isBridged
                ? {
                      isBridged: true,
                      sourceProtocol: 'atprotocol',
                      activityPubHandle: qsBridge.activityPubHandle,
                  }
                : undefined,
        },
        likeUri: null,
        repostUri: null,
        isActivityPub: true,
        protocol: 'pixelfed',
    }
}

/**
 * Fetch profile posts directly from a Pixelfed instance.
 *
 * Parses the handle, looks up the account ID, fetches statuses, and maps them.
 * Account IDs are cached in-memory to avoid redundant lookups during pagination.
 */
export async function fetchPixelfedProfilePosts(
    handle: string,
    options?: { cursor?: string; limit?: number; pixelfedSession?: PixelfedSession }
): Promise<PostsPage> {
    const instanceUrl = instanceUrlFromHandle(handle)

    // The account/status JSON below may come directly from the viewed profile's own
    // (potentially hostile) instance rather than a party we trust to have done real
    // webfinger-based discovery — see apBridgedIdentity.ts's module docblock (#2226
    // follow-up). Only honor the acct/username bridge cross-check when the fetch is
    // known-trusted: same-origin-as-session, or Bridgy Fed's own server.
    const isTrustedOrigin = isTrustedBridgeOrigin(
        instanceUrl,
        options?.pixelfedSession?.instance_url
    )

    // Strip leading @ if present (e.g., "@alice@pixelfed.social" -> "alice")
    const username = handle.replace(/^@/, '').split('@')[0]

    // Check account cache (includes display name and avatar for profileAuthor)
    const cacheKey = `${instanceUrl}:${username}`
    let cached = accountCache.get(cacheKey)

    if (!cached) {
        const account = await lookupAccountOnInstance(instanceUrl, username)
        cached = {
            id: account.id,
            displayName: account.display_name || undefined,
            avatarUrl: account.avatar || undefined,
        }
        setCachedAccount(cacheKey, cached)
    }

    // Parse cursor: for the first page, cursor is undefined.
    // For subsequent pages, cursor is the max_id extracted from the Link header.
    const fetchOptions: { maxId?: string; limit?: number; accessToken?: string } = {
        limit: options?.limit ?? DEFAULT_LIMIT,
    }
    if (options?.cursor) {
        fetchOptions.maxId = options.cursor
    }

    // Use authenticated fetch when the profile is on the user's own instance
    // so the API returns engagement state (favourited/reblogged)
    if (options?.pixelfedSession) {
        try {
            const sessionInstanceUrl = new URL(options.pixelfedSession.instance_url).origin
            const profileInstanceUrl = new URL(instanceUrl).origin
            if (sessionInstanceUrl === profileInstanceUrl) {
                fetchOptions.accessToken = options.pixelfedSession.access_token
            }
        } catch {
            console.warn('[pixelfed] Failed to parse instance URL for authenticated fetch')
        }
    }

    const { statuses, nextUrl } = await fetchPixelfedStatuses(instanceUrl, cached.id, fetchOptions)

    const profileAuthor: Post['author'] = {
        did: '',
        handle,
        displayName: cached.displayName,
        avatarUrl: cached.avatarUrl,
    }

    const posts = statuses.map(s =>
        mapPixelfedStatus(s, instanceUrl, profileAuthor, isTrustedOrigin)
    )

    // Extract cursor from the next page URL's max_id param
    let cursor: string | undefined
    if (nextUrl) {
        try {
            const nextUrlParams = new URL(nextUrl).searchParams
            cursor = nextUrlParams.get('max_id') ?? undefined
        } catch {
            // If URL parsing fails, use the last status ID as cursor
            if (statuses.length > 0) {
                cursor = statuses[statuses.length - 1].id
            }
        }
    }

    return { posts, cursor }
}

// --- Shared Fetch Helper ---

/**
 * Fetch a JSON response from a Pixelfed instance endpoint with timeout and SSRF protection.
 */
async function fetchPixelfedEndpoint<T>(
    instanceUrl: string,
    path: string,
    accessToken?: string
): Promise<T> {
    const hostname = new URL(instanceUrl).hostname
    if (!isSafeHostname(hostname)) {
        throw new Error(`Unsafe hostname: ${hostname}`)
    }

    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS)

    try {
        const headers: Record<string, string> = {}
        if (accessToken) {
            headers.Authorization = `Bearer ${accessToken}`
        }
        const res = await fetch(`${instanceUrl}${path}`, { headers, signal: controller.signal })

        if (!res.ok) {
            throw new Error(`Pixelfed API error: ${res.status}`)
        }

        return res.json() as Promise<T>
    } finally {
        clearTimeout(timeout)
    }
}

// --- Thread Fetching ---

/**
 * Fetch a single status by ID from a Pixelfed instance (unauthenticated).
 */
export async function fetchPixelfedStatusById(
    instanceUrl: string,
    statusId: string,
    accessToken?: string
): Promise<MastodonStatus> {
    return fetchPixelfedEndpoint<MastodonStatus>(
        instanceUrl,
        `/api/v1/statuses/${encodeURIComponent(statusId)}`,
        accessToken
    )
}

/**
 * Fetch thread context (ancestors + descendants) for a status.
 * When accessToken is provided, the response includes favourited/reblogged state.
 */
export async function fetchPixelfedContext(
    instanceUrl: string,
    statusId: string,
    accessToken?: string
): Promise<MastodonContext> {
    return fetchPixelfedEndpoint<MastodonContext>(
        instanceUrl,
        `/api/v1/statuses/${encodeURIComponent(statusId)}/context`,
        accessToken
    )
}

/**
 * Compute reply depth and replyingTo metadata for descendants.
 *
 * Pixelfed returns descendants as a flat list (same as Mastodon). We build a
 * depth map from in_reply_to_id chains, starting at the main post (depth 0).
 */
export function buildThreadReplies(
    mainStatusId: string,
    descendants: MastodonStatus[],
    fetchedInstanceUrl: string,
    isTrustedOrigin = true
): ThreadPost[] {
    // Map status ID -> status for quick lookup
    const statusById = new Map<string, MastodonStatus>()
    for (const s of descendants) {
        statusById.set(s.id, s)
    }

    // Map status ID -> depth (main post's direct replies are depth 0)
    const depthMap = new Map<string, number>()
    depthMap.set(mainStatusId, -1) // Main post itself is "above" depth 0

    // Compute depths in a single pass (descendants are in chronological order,
    // so parents always appear before their children)
    for (const s of descendants) {
        const parentId = s.in_reply_to_id
        if (parentId && depthMap.has(parentId)) {
            depthMap.set(s.id, depthMap.get(parentId)! + 1)
        } else {
            // Parent not in our set -- treat as direct reply to main post
            depthMap.set(s.id, 0)
        }
    }

    return descendants.map(s => {
        const depth = depthMap.get(s.id) ?? 0

        // Build replyingTo for replies that aren't direct replies to the main post
        let replyingTo: ThreadPost['replyingTo']
        if (s.in_reply_to_id && s.in_reply_to_id !== mainStatusId) {
            const parent = statusById.get(s.in_reply_to_id)
            if (parent) {
                const parentNormalized = resolveAcctHandle(
                    parent.account.acct,
                    fetchedInstanceUrl,
                    parent.account.username,
                    isTrustedOrigin
                )
                replyingTo = {
                    uri: parent.uri,
                    authorHandle: parentNormalized,
                    authorDisplayName: parent.account.display_name || undefined,
                }
            }
        }

        return {
            post: mapPixelfedStatus(s, fetchedInstanceUrl, undefined, isTrustedOrigin),
            replyDepth: depth,
            replyingTo,
        }
    })
}

/**
 * Fetch a complete AP thread directly from a Pixelfed instance.
 *
 * Makes two API calls: one for the status itself, one for the context
 * (ancestors + descendants). This replaces N+1 GraphQL queries with 2 HTTP calls.
 *
 * @param instanceUrl - The instance base URL (e.g., "https://pixelfed.social")
 * @param statusId - The numeric Pixelfed status ID
 */
export async function fetchPixelfedThread(
    instanceUrl: string,
    statusId: string,
    pixelfedSession?: PixelfedSession
): Promise<Thread> {
    // Use authenticated requests when the thread is on the user's own instance
    // so the API returns favourited/reblogged engagement state.
    let accessToken: string | undefined
    if (pixelfedSession) {
        try {
            const session = await ensureValidSession(pixelfedSession)
            const sessionOrigin = new URL(session.instance_url).origin
            const threadOrigin = new URL(instanceUrl).origin
            if (sessionOrigin === threadOrigin) {
                accessToken = session.access_token
            }
        } catch {
            // Fall through to unauthenticated fetch
        }
    }

    // The account/status JSON below may come directly from the thread's own
    // (potentially hostile) instance rather than a party we trust to have done real
    // webfinger-based discovery — see apBridgedIdentity.ts's module docblock (#2226
    // follow-up). Only honor the acct/username bridge cross-check when the fetch is
    // known-trusted: the same-origin check mirrors the accessToken gate above, and
    // isTrustedBridgeOrigin separately allows Bridgy Fed's own server.
    const isTrustedOrigin = isTrustedBridgeOrigin(instanceUrl, pixelfedSession?.instance_url)

    // Fetch the main status and context in parallel
    const [mainStatus, context] = await Promise.all([
        fetchPixelfedStatusById(instanceUrl, statusId, accessToken),
        fetchPixelfedContext(instanceUrl, statusId, accessToken),
    ])

    const mainPost = mapPixelfedStatus(mainStatus, instanceUrl, undefined, isTrustedOrigin)

    // Map ancestors to parent chain (already in chronological order from API)
    const parentChain: ThreadPost[] = context.ancestors.map(s => ({
        post: mapPixelfedStatus(s, instanceUrl, undefined, isTrustedOrigin),
        replyDepth: 0,
        replyingTo: undefined,
    }))

    // Build reply list with computed depths
    const replies = buildThreadReplies(
        mainStatus.id,
        context.descendants,
        instanceUrl,
        isTrustedOrigin
    )

    return {
        post: {
            post: mainPost,
            replyDepth: 0,
            replyingTo: undefined,
        },
        parentChain,
        replies,
        replyCount: mainStatus.replies_count ?? 0,
    }
}

// --- Status Management ---

/**
 * Delete a status on the user's Pixelfed instance.
 *
 * Requires authenticated session -- the status must belong to the authenticated user.
 */
export async function deletePixelfedStatus(
    pixelfedSession: PixelfedSession,
    statusId: string
): Promise<void> {
    const session = await ensureValidSession(pixelfedSession)

    const parsed = new URL(session.instance_url)
    if (parsed.protocol !== 'https:') {
        throw new Error(`Unsafe URL scheme: ${parsed.protocol}`)
    }
    if (!isSafeHostname(parsed.hostname)) {
        throw new Error(`Unsafe hostname: ${parsed.hostname}`)
    }

    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS)

    try {
        const res = await fetch(
            `${session.instance_url}/api/v1/statuses/${encodeURIComponent(statusId)}`,
            {
                method: 'DELETE',
                headers: {
                    Authorization: `Bearer ${session.access_token}`,
                },
                signal: controller.signal,
            }
        )

        if (!res.ok) {
            throw new Error(`Failed to delete status: ${res.status}`)
        }
    } finally {
        clearTimeout(timeout)
    }
}

// --- Engagement Actions ---

/**
 * Perform an authenticated action on a Pixelfed status (favourite, reblog, etc.).
 *
 * Validates session, applies SSRF protection, and sends a POST request.
 */
type PixelfedStatusAction =
    | 'favourite'
    | 'unfavourite'
    | 'reblog'
    | 'unreblog'
    | 'bookmark'
    | 'unbookmark'

async function pixelfedStatusAction(
    pixelfedSession: PixelfedSession,
    statusId: string,
    action: PixelfedStatusAction
): Promise<void> {
    const session = await ensureValidSession(pixelfedSession)

    const parsed = new URL(session.instance_url)
    if (parsed.protocol !== 'https:') {
        throw new Error(`Unsafe URL scheme: ${parsed.protocol}`)
    }
    if (!isSafeHostname(parsed.hostname)) {
        throw new Error(`Unsafe hostname: ${parsed.hostname}`)
    }

    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS)

    try {
        const res = await fetch(
            `${session.instance_url}/api/v1/statuses/${encodeURIComponent(statusId)}/${action}`,
            {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${session.access_token}`,
                },
                signal: controller.signal,
            }
        )

        if (!res.ok) {
            throw new Error(`Failed to ${action} status: ${res.status}`)
        }
    } finally {
        clearTimeout(timeout)
    }
}

/** Favourite a status on the user's Pixelfed instance. */
export async function favouritePixelfedStatus(
    pixelfedSession: PixelfedSession,
    statusId: string
): Promise<void> {
    return pixelfedStatusAction(pixelfedSession, statusId, 'favourite')
}

/** Unfavourite a status on the user's Pixelfed instance. */
export async function unfavouritePixelfedStatus(
    pixelfedSession: PixelfedSession,
    statusId: string
): Promise<void> {
    return pixelfedStatusAction(pixelfedSession, statusId, 'unfavourite')
}

/** Reblog a status on the user's Pixelfed instance. */
export async function reblogPixelfedStatus(
    pixelfedSession: PixelfedSession,
    statusId: string
): Promise<void> {
    return pixelfedStatusAction(pixelfedSession, statusId, 'reblog')
}

/** Unreblog a status on the user's Pixelfed instance. */
export async function unreblogPixelfedStatus(
    pixelfedSession: PixelfedSession,
    statusId: string
): Promise<void> {
    return pixelfedStatusAction(pixelfedSession, statusId, 'unreblog')
}

/**
 * Bookmark a status on the user's Pixelfed instance.
 *
 * Pixelfed implements the Mastodon API, so after validating the pixelfed session
 * we delegate to the shared Mastodon bookmark implementation.
 */
export async function bookmarkPixelfedStatus(
    pixelfedSession: PixelfedSession,
    statusId: string
): Promise<void> {
    const session = await ensureValidSession(pixelfedSession)
    return bookmarkMastodonStatus(session as unknown as MastodonSession, statusId)
}

/**
 * Remove a bookmark from a status on the user's Pixelfed instance.
 *
 * Pixelfed implements the Mastodon API, so after validating the pixelfed session
 * we delegate to the shared Mastodon unbookmark implementation.
 */
export async function unbookmarkPixelfedStatus(
    pixelfedSession: PixelfedSession,
    statusId: string
): Promise<void> {
    const session = await ensureValidSession(pixelfedSession)
    return unbookmarkMastodonStatus(session as unknown as MastodonSession, statusId)
}

/**
 * Fetch the authenticated user's bookmarked statuses on Pixelfed.
 *
 * Uses the shared fetchAPBookmarks helper with the Pixelfed status mapper.
 */
export async function fetchPixelfedBookmarks(
    pixelfedSession: PixelfedSession,
    options?: { cursor?: string; limit?: number }
): Promise<PostsPage> {
    const session = await ensureValidSession(pixelfedSession)
    return fetchAPBookmarks(
        session,
        options,
        s => mapPixelfedStatus(s, session.instance_url),
        'pixelfed'
    )
}

/**
 * Clear the account cache (useful for testing)
 */
export function clearAccountCache(): void {
    accountCache.clear()
}
